import React from 'react'
import type { GalleryListItem } from '../../api/galleryApi'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  ExternalLink,
  Calendar,
  MapPin,
  Briefcase,
  CheckCircle2,
  Clock,
  XCircle,
  Sparkles,
  Image as ImageIcon
} from 'lucide-react'
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip'
import { getEnrichmentDisplayState } from '../../utils/enrichment'
import { cn } from '@/lib/utils'

/**
 * Formats a date string for display
 * Shows relative time for recent dates, full date for older ones
 */
function formatGalleryDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return '-'

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`

  // For older dates, show the actual date
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

/** Returns month/year in compact format for subtle date display */
function formatCompactDate(dateStr: string | null | undefined): { month: string; year: string } | null {
  if (!dateStr) return null
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return null
  return {
    month: date.toLocaleDateString('en-CA', { month: 'short' }).toUpperCase(),
    year: date.getFullYear().toString()
  }
}

export interface GalleryListProps {
  items: GalleryListItem[]
  view: 'grid' | 'table'
  onSelect?: (id: number) => void
  loading?: boolean
  selectedIds?: Set<number>
  onToggleSelect?: (id: number) => void
  coverById?: Record<number, string | undefined>
}

const EnrichmentBadge = ({
  meta,
  align = 'left'
}: {
  meta?: GalleryListItem['gallery_jobber_details'] | null
  align?: 'left' | 'right'
}) => {
  const state = getEnrichmentDisplayState(meta)
  return (
    <div className={`text-xs ${align === 'right' ? 'text-right' : ''}`}>
      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${state.pillClass}`}>
        {state.label}
      </span>
      {state.description && (
        <p className="mt-0.5 text-[11px] text-muted-foreground">{state.description}</p>
      )}
    </div>
  )
}

export default function GalleryList({ items, view, onSelect, loading, selectedIds, onToggleSelect, coverById }: GalleryListProps) {
  /**
   * Status indicator with icon and refined styling
   * Uses semantic colors that complement the FixMyBrick brand palette
   */
  const StatusIndicator = ({ status, compact = false }: { status: string; compact?: boolean }) => {
    const norm = (status || '').toLowerCase()

    const config = {
      approved: {
        icon: CheckCircle2,
        label: 'Approved',
        classes: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-400 dark:border-emerald-800'
      },
      rejected: {
        icon: XCircle,
        label: 'Rejected',
        classes: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/50 dark:text-rose-400 dark:border-rose-800'
      },
      pending: {
        icon: Clock,
        label: 'Pending',
        classes: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/50 dark:text-amber-400 dark:border-amber-800'
      }
    }[norm] || {
      icon: Clock,
      label: status || 'Unknown',
      classes: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
    }

    const Icon = config.icon

    if (compact) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={cn(
                'inline-flex items-center justify-center w-6 h-6 rounded-full border transition-transform hover:scale-110',
                config.classes
              )}>
                <Icon className='w-3.5 h-3.5' />
              </div>
            </TooltipTrigger>
            <TooltipContent side='left'>{config.label}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    }

    return (
      <div className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
        config.classes
      )}>
        <Icon className='w-3 h-3' />
        {config.label}
      </div>
    )
  }

  // Legacy StatusBadge for table view compatibility
  const StatusBadge = ({ status }: { status: string }) => <StatusIndicator status={status} />

  // Only hijack plain left-clicks for SPA navigation; allow modifier/middle clicks for new tab/window
  const handleAnchorClick = (e: React.MouseEvent, id: number) => {
    if (
      e.defaultPrevented ||
      e.button !== 0 ||
      e.metaKey ||
      e.ctrlKey ||
      e.shiftKey ||
      e.altKey
    ) {
      // Let the browser handle (open in new tab/window)
      return
    }
    e.preventDefault()
    onSelect?.(id)
  }

  if (view === 'table') {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='w-8'></TableHead>
            <TableHead>Title</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Enrichment</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && items.length === 0 && (
            Array.from({ length: 8 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><div className='h-4 w-4 rounded bg-muted animate-pulse' /></TableCell>
                <TableCell><div className='h-4 w-40 rounded bg-muted animate-pulse' /></TableCell>
                <TableCell><div className='h-4 w-20 rounded bg-muted animate-pulse' /></TableCell>
                <TableCell><div className='h-4 w-20 rounded bg-muted animate-pulse' /></TableCell>
                <TableCell><div className='h-4 w-16 rounded bg-muted animate-pulse' /></TableCell>
                <TableCell><div className='h-4 w-24 rounded bg-muted animate-pulse' /></TableCell>
              </TableRow>
            ))
          )}
          {!loading && items.length === 0 && (
            <TableRow>
              <TableCell colSpan={6}>
                <div className='text-sm text-muted-foreground'>No galleries found</div>
              </TableCell>
            </TableRow>
          )}
          {items.map(g => (
            <TableRow key={g.id} className='cursor-pointer' onClick={() => onSelect?.(g.id)}>
              <TableCell onClick={(e) => e.stopPropagation()}>
                <input
                  type='checkbox'
                  checked={selectedIds?.has(g.id) || false}
                  onChange={() => onToggleSelect?.(g.id)}
                  aria-label='Select row'
                />
              </TableCell>
              <TableCell>
                <div className='flex items-center gap-2 min-w-0'>
                  <a
                    href={`/media/galleries/${g.id}`}
                    onClick={(e) => handleAnchorClick(e, g.id)}
                    className='hover:underline truncate'
                    title={g.title}
                  >
                    {g.title}
                  </a>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button asChild size='xs' variant='outline' className='text-primary border-primary/30 hover:bg-primary/10'>
                          <a
                            href={`/media/galleries/${g.id}`}
                            target='_blank'
                            rel='noopener noreferrer'
                            onClick={(e) => e.stopPropagation()}
                            aria-label='Open in new tab'
                          >
                            <ExternalLink className='h-4 w-4' />
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Open in new tab</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {g.description ? (
                  <div className='text-xs text-muted-foreground line-clamp-1'>{g.description}</div>
                ) : null}
              </TableCell>
              <TableCell>
                {g.franchise_region ? <Badge variant='outline'>{g.franchise_region}</Badge> : <span className='text-muted-foreground'>-</span>}
              </TableCell>
              <TableCell>
                <span className='text-sm text-muted-foreground' title={g.created_on || undefined}>
                  {formatGalleryDate(g.created_on)}
                </span>
              </TableCell>
              <TableCell>
                <StatusBadge status={g.review_status as string} />
              </TableCell>
              <TableCell>
                <EnrichmentBadge meta={g.gallery_jobber_details} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // GRID VIEW: Industrial-Refined Gallery Cards
  // Photo-forward with architectural precision and warm masonry accents
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {/* Loading Skeleton - Refined shimmer effect */}
      {loading && items.length === 0 && (
        Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-100 to-slate-50 dark:from-slate-800 dark:to-slate-900"
            style={{ animationDelay: `${i * 75}ms` }}
          >
            <div className="aspect-[4/3] animate-pulse bg-gradient-to-r from-transparent via-slate-200/50 to-transparent dark:via-slate-700/50"
                 style={{ backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
            <div className="p-4 space-y-3">
              <div className="h-4 w-3/4 rounded-md bg-slate-200 dark:bg-slate-700 animate-pulse" />
              <div className="h-3 w-1/2 rounded-md bg-slate-100 dark:bg-slate-800 animate-pulse" />
              <div className="flex gap-2">
                <div className="h-6 w-16 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse" />
                <div className="h-6 w-20 rounded-full bg-slate-100 dark:bg-slate-800 animate-pulse" />
              </div>
            </div>
          </div>
        ))
      )}

      {/* Empty State */}
      {!loading && items.length === 0 && (
        <div className='col-span-full flex flex-col items-center justify-center py-16 text-center'>
          <div className='w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4'>
            <ImageIcon className='w-8 h-8 text-slate-400' />
          </div>
          <p className='text-lg font-medium text-slate-600 dark:text-slate-400'>No galleries found</p>
          <p className='text-sm text-muted-foreground mt-1'>Try adjusting your filters or search terms</p>
        </div>
      )}

      {/* Gallery Cards */}
      {items.map((g, index) => {
        const cover = coverById?.[g.id]
        const isSelected = selectedIds?.has(g.id) || false
        const dateInfo = formatCompactDate(g.created_on)
        const enrichmentState = getEnrichmentDisplayState(g.gallery_jobber_details)

        return (
          <a
            key={g.id}
            href={`/media/galleries/${g.id}`}
            onClick={(e) => handleAnchorClick(e, g.id)}
            className="group relative block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <article
              className={cn(
                // Base card styling with industrial-refined aesthetic
                'relative overflow-hidden rounded-xl transition-all duration-300',
                'bg-white dark:bg-slate-900',
                'border border-slate-200/80 dark:border-slate-700/80',
                // Elevated hover state with smooth shadow transition
                'shadow-sm hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-black/20',
                'hover:-translate-y-1',
                // Selection state
                isSelected && 'ring-2 ring-primary ring-offset-2 dark:ring-offset-slate-950'
              )}
            >
              {/* ═══════════════════════════════════════════════════════════════
                  IMAGE SECTION: Hero visual with gradient overlay
                  ═══════════════════════════════════════════════════════════════ */}
              <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
                {cover ? (
                  <>
                    <img
                      src={cover}
                      alt={g.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
                    />
                    {/* Subtle vignette overlay for depth */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300" />
                  </>
                ) : (
                  // Elegant placeholder with brick pattern hint
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="w-14 h-14 rounded-xl bg-slate-300/50 dark:bg-slate-700/50 flex items-center justify-center backdrop-blur-sm">
                      <ImageIcon className="w-7 h-7 text-slate-400 dark:text-slate-500" />
                    </div>
                    <span className="mt-2 text-xs text-slate-400 dark:text-slate-500 font-medium">No cover image</span>
                  </div>
                )}

                {/* ─────────────────────────────────────────────────────────────
                    TOP CONTROLS: Selection checkbox + External link
                    ───────────────────────────────────────────────────────────── */}
                <div className="absolute top-3 left-3 z-10" onClick={(e) => { e.preventDefault(); e.stopPropagation() }}>
                  <label className={cn(
                    'flex items-center justify-center w-6 h-6 rounded-md cursor-pointer transition-all duration-200',
                    'bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm',
                    'border border-slate-200 dark:border-slate-700',
                    'hover:bg-white dark:hover:bg-slate-900 hover:scale-110',
                    isSelected && 'bg-primary border-primary'
                  )}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect?.(g.id)}
                      className="sr-only"
                      aria-label={`Select ${g.title}`}
                    />
                    {isSelected ? (
                      <CheckCircle2 className="w-4 h-4 text-white" />
                    ) : (
                      <div className="w-3 h-3 rounded-sm border-2 border-slate-300 dark:border-slate-600" />
                    )}
                  </label>
                </div>

                <div className="absolute top-3 right-3 z-10 flex items-center gap-2">
                  {/* Status indicator - compact version on image */}
                  <StatusIndicator status={g.review_status as string} compact />

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          asChild
                          size="icon"
                          variant="secondary"
                          className="w-7 h-7 bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm hover:bg-white dark:hover:bg-slate-900 hover:scale-110 transition-all duration-200 border border-slate-200/50 dark:border-slate-700/50"
                          aria-label="Open in new tab"
                        >
                          <a
                            href={`/media/galleries/${g.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-slate-600 dark:text-slate-400" />
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">Open in new tab</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* ─────────────────────────────────────────────────────────────
                    DATE BADGE: Architectural corner element (when date exists)
                    ───────────────────────────────────────────────────────────── */}
                {dateInfo && (
                  <div className="absolute bottom-3 left-3 z-10">
                    <div className="flex flex-col items-center px-2 py-1.5 rounded-lg bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50 shadow-sm">
                      <span className="text-[10px] font-bold tracking-wider text-primary dark:text-primary/90">
                        {dateInfo.month}
                      </span>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400 -mt-0.5">
                        {dateInfo.year}
                      </span>
                    </div>
                  </div>
                )}

                {/* Job number badge - bottom right */}
                {g.job_number && (
                  <div className="absolute bottom-3 right-3 z-10">
                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-900/80 dark:bg-white/90 backdrop-blur-sm text-white dark:text-slate-900 text-xs font-medium">
                      <Briefcase className="w-3 h-3" />
                      #{g.job_number}
                    </div>
                  </div>
                )}
              </div>

              {/* ═══════════════════════════════════════════════════════════════
                  CONTENT SECTION: Title, metadata, and enrichment status
                  ═══════════════════════════════════════════════════════════════ */}
              <div className="p-4">
                {/* Title */}
                <h3 className="font-semibold text-slate-900 dark:text-slate-100 truncate group-hover:text-primary transition-colors duration-200">
                  {g.title}
                </h3>

                {/* Description */}
                {g.description && (
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 line-clamp-2 leading-relaxed">
                    {g.description}
                  </p>
                )}

                {/* ─────────────────────────────────────────────────────────────
                    META ROW: Region, enrichment, date (fallback if no image date)
                    ───────────────────────────────────────────────────────────── */}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  {/* Region badge with location icon */}
                  {g.franchise_region && (
                    <Badge
                      variant="outline"
                      className="gap-1 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 font-medium"
                    >
                      <MapPin className="w-3 h-3" />
                      {g.franchise_region}
                    </Badge>
                  )}

                  {/* Enrichment status with refined styling */}
                  {enrichmentState.label !== 'Not enriched' && (
                    <div className={cn(
                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
                      enrichmentState.label === 'Enriched'
                        ? 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-400 dark:border-violet-800'
                        : 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                    )}>
                      <Sparkles className="w-3 h-3" />
                      {enrichmentState.label}
                    </div>
                  )}

                  {/* Relative date (shown only if no dateInfo was displayed on image) */}
                  {!dateInfo && g.created_on && (
                    <span className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {formatGalleryDate(g.created_on)}
                    </span>
                  )}
                </div>
              </div>

              {/* ─────────────────────────────────────────────────────────────
                  HOVER ACCENT: Subtle brick-red bottom border on hover
                  ───────────────────────────────────────────────────────────── */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/0 via-primary to-primary/0 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-center" />
            </article>
          </a>
        )
      })}

      {/* Shimmer animation keyframes injected via style tag */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  )
}
